/* ══════════════════════════════════════════════════════════════════════════
   ALIA booking widget — "Wax Seal" launcher + "Request a date" enquiry form.
   Corporate is the priority action and opens the lead form.

   Shadow-DOM isolated. The launcher/envelope use the theme's :root tokens
   (inherited through the shadow boundary); the modal carries the enquiry
   form's own dark design tokens, ported from the approved standalone form.

   Leads post to the ONE shared ALIA Google Apps Script Web App. The payload
   sends every value under BOTH the sheet's header text and a snake_case
   key, so it works against any store's header-mapping script. The POST is
   mode:'no-cors' — the response is opaque, so a shown confirmation only
   proves the request left the browser. The sheet is the only proof.
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  if (document.getElementById('alia-wgt-host')) return;

  /* ── WIRING ─────────────────────────────────────────────────────────── */
  var LEAD_ENDPOINT = 'https://script.google.com/macros/s/AKfycbymDImriMbjGLCHuvihZT-KhSDkHBb_sdlKqkOeaBY8bV-QbQivPefA2f79lKe5FOvFGw/exec';
  var PHONE_HREF    = 'tel:+15145662542';
  var PHONE_LABEL   = '(514) 566-2542';
  var OPENTABLE_URL = 'https://www.opentable.ca/r/alia-montreal';

  /* Site name comes from Liquid via a non-deferred config tag; the hostname
     is only the fallback. A sheet shared across stores needs the store NAME. */
  var CFG = window.ALIA_WIDGET_CONFIG || {};

  /* Sheet header text ↔ snake_case key. "Received" is stamped server-side. */
  var FIELDS = [
    ['Name',           'name'],
    ['Company',        'company'],
    ['Email',          'email'],
    ['Phone',          'phone'],
    ['Event type',     'event_type'],
    ['Event detail',   'event_detail'],
    ['Guests',         'guests'],
    ['Preferred date', 'date'],
    ['Budget per head','budget'],
    ['Notes',          'notes']
  ];
  var LABELS = {
    name: 'Name', company: 'Company', email: 'Work email', phone: 'Phone',
    event_type: 'Event', event_detail: 'Event detail', guests: 'Guests',
    date: 'Preferred date', budget: 'Budget per head', notes: 'Notes'
  };

  var CSS = `
  :host{
    /* theme tokens for the launcher + envelope */
    --w-parchment:var(--parchment,#EAE9DC);
    --w-bone:var(--bone,#EEEDE3);
    --w-espresso:var(--espresso,#584737);
    --w-roast:var(--roast,#4A331F);
    --w-char:var(--char,#241A11);
    --w-amber:var(--amber,#8A5F3A);
    --w-bone-rgb:var(--bone-rgb,238,237,227);
    --w-char-rgb:var(--char-rgb,36,26,17);
    --w-amber-rgb:var(--amber-rgb,138,95,58);
    --w-green:var(--cta-green,#1FA35B);
    --w-green-bright:var(--cta-green-bright,#27C06D);
    --w-green-ink:var(--cta-green-ink,#08120B);
    --w-ease:var(--ease-soft,cubic-bezier(.22,.61,.36,1));
    --w-rise:var(--ease-rise,cubic-bezier(.16,1,.3,1));

    /* enquiry-form design tokens (approved standalone form, measured there:
       placeholder 5.9:1 on the well, brass rim 3.24:1 on the card) */
    --night:#100E0A; --card:#17140F; --hair:#2A241A;
    --cream:#F3EEE1; --cream-dim:#A79E8C;
    --brass:#C9A45C; --brass-edge:#7A6437; --brass-lit:#9C8046;
    --well:#3D3428; --well-hover:#4A4031; --well-focus:#514534;
    --placeholder:#BDB4A2;
    --jade:#34A96B; --jade-lit:#46C983; --jade-ink:#04210F;
    --ember:#C4713F; --ember-lit:#E0705E;

    --ff:'Chivo',system-ui,sans-serif;
    --mono:'Chivo Mono',monospace;

    position:fixed; inset:0; z-index:9400; pointer-events:none;
    font-family:var(--ff); font-weight:300; line-height:1.6;
    -webkit-font-smoothing:antialiased;
  }
  *{box-sizing:border-box;margin:0;padding:0}
  button{font-family:inherit;color:inherit;background:none;border:none;cursor:pointer}
  :focus-visible{outline:2px solid var(--jade-lit);outline-offset:3px}
  /* the UA rule for [hidden] is only display:none — any author display value
     on the same element silently outranks it. Guard the whole bug class. */
  [hidden]{display:none !important}

  /* ── LAUNCHER + ENVELOPE (theme language) ───────────────────────────── */
  .w-wax{
    position:absolute;
    right:clamp(.9rem,2.5vw,1.6rem);
    bottom:clamp(.9rem,2.5vw,1.6rem);
    display:flex; flex-direction:column; align-items:flex-end;
    pointer-events:auto;
    opacity:0; transform:translateY(14px);
    transition:opacity .5s var(--w-ease),transform .5s var(--w-ease);
  }
  .w-wax.revealed{opacity:1;transform:none}
  .wax-env{
    position:absolute; right:0; bottom:calc(100% + .85rem);
    width:min(268px,calc(100vw - 1.8rem));
    background:var(--w-espresso);
    color:var(--w-bone);
    border:1px solid rgba(var(--w-bone-rgb),.22);
    border-radius:3px;
    box-shadow:0 22px 52px rgba(var(--w-char-rgb),.34);
    padding:0 1.15rem;
    text-align:center;
    max-height:0; opacity:0; overflow:hidden; pointer-events:none;
    transform:translateY(10px); transform-origin:bottom right;
    transition:max-height .5s var(--w-rise),opacity .3s var(--w-ease),
               transform .5s var(--w-rise),padding .5s var(--w-rise);
  }
  .w-wax.open .wax-env{
    max-height:420px; opacity:1; transform:none; pointer-events:auto;
    padding:1.3rem 1.15rem 1.15rem;
  }
  .wax-eyebrow{
    font-family:var(--mono); font-weight:400;
    font-size:var(--fs-label,.68rem);
    letter-spacing:.42em; text-transform:uppercase;
    color:var(--w-amber);
    display:flex; align-items:center; justify-content:center; gap:.6rem;
    padding-left:.42em;
  }
  .wax-eyebrow i{
    width:7px;height:7px;border-radius:50%;flex:none;
    background:var(--w-amber);box-shadow:0 0 10px var(--w-amber);
    animation:wpulse 2s ease-in-out infinite;
  }
  @keyframes wpulse{0%,100%{opacity:1}50%{opacity:.42}}
  .wax-title{
    font-weight:300; text-transform:uppercase;
    font-size:1.5rem; line-height:.98; letter-spacing:.02em;
    margin:.75rem 0 1.15rem;
  }
  .wax-card{display:flex;flex-direction:column;gap:.55rem;text-align:left}
  .cta{
    display:inline-flex; align-items:center; gap:.7rem;
    font-family:var(--mono); font-size:.72rem;
    letter-spacing:.22em; text-transform:uppercase;
    padding:.95rem 1.15rem; border-radius:2px; width:100%;
    transition:transform .4s var(--w-ease),background .35s,color .35s,border-color .35s;
    text-decoration:none;
  }
  .cta .lab{display:flex;flex-direction:column;gap:.28rem;text-align:left;line-height:1.15}
  .cta .sub{font-size:.58rem;letter-spacing:.24em;opacity:.72;font-weight:400}
  .cta .dot{width:6px;height:6px;border-radius:50%;flex:none;background:var(--w-amber);box-shadow:0 0 8px var(--w-amber)}
  .cta.primary{background:var(--w-green);color:var(--w-green-ink)}
  .cta.primary .dot{background:var(--w-green-ink);box-shadow:none}
  .cta.primary:hover{background:var(--w-green-bright);transform:translateY(-3px)}
  .cta.ghost{background:transparent;color:var(--w-bone);border:1px solid rgba(var(--w-bone-rgb),.5)}
  .cta.ghost:hover{border-color:var(--w-bone);transform:translateY(-3px)}

  .wax-seal{
    width:58px;height:58px;border-radius:50%;
    background:radial-gradient(circle at 38% 32%,var(--w-green-bright),var(--w-green));
    border:1px solid rgba(8,18,11,.35);
    display:grid;place-items:center;
    font-family:var(--mono);font-weight:400;font-size:1.3rem;
    color:var(--w-green-ink);
    box-shadow:0 0 24px rgba(39,192,109,.55),
               0 14px 30px rgba(var(--w-char-rgb),.4),
               inset 0 1px 0 rgba(255,255,255,.25);
    transition:transform .45s var(--w-rise),box-shadow .35s var(--w-ease);
    animation:sglow 2.6s ease-in-out infinite;
  }
  @keyframes sglow{
    0%,100%{box-shadow:0 0 18px rgba(39,192,109,.45),0 14px 30px rgba(var(--w-char-rgb),.4),inset 0 1px 0 rgba(255,255,255,.25)}
    50%{box-shadow:0 0 36px rgba(39,192,109,.85),0 14px 30px rgba(var(--w-char-rgb),.4),inset 0 1px 0 rgba(255,255,255,.25)}
  }
  .wax-seal:hover{transform:translateY(-3px) scale(1.03)}
  .w-wax.open .wax-seal{transform:scale(.82) rotate(-9deg)}
  .w-wax.open .wax-seal:hover{transform:scale(.86) rotate(-9deg) translateY(-2px)}
  .wax-hint{
    font-family:var(--mono);font-size:.6rem;
    letter-spacing:.28em;text-transform:uppercase;
    color:var(--w-amber);margin-top:.6rem;
    text-align:right; white-space:nowrap;
    transition:opacity .3s var(--w-ease);
  }
  .w-wax.open .wax-hint{opacity:0;pointer-events:none}

  /* ── SCRIM + MODAL (enquiry-form design) ────────────────────────────── */
  .scrim{
    position:fixed;inset:0;z-index:20;
    background:rgba(16,14,10,.78);
    backdrop-filter:blur(11px) saturate(1.1);
    -webkit-backdrop-filter:blur(11px) saturate(1.1);
    display:none;align-items:center;justify-content:center;
    padding:.8rem;pointer-events:auto;overflow:hidden;
  }
  .scrim.on{display:flex}
  .modal{
    position:relative;width:min(660px,100%);
    max-height:calc(100dvh - 1.6rem);overflow-y:auto;
    /* no visible scrollbar — the height tiers make the content fit one
       view; wheel/touch scrolling stays as an invisible last resort so
       nothing is ever unreachable on a pathologically short window */
    scrollbar-width:none;
    background:var(--card);color:var(--cream);
    border:1px solid var(--hair);border-top:2px solid var(--jade);
    border-radius:5px;
    font-size:16px;
    color-scheme:dark; /* light date-picker glyph on the dark well */
    animation:wrise .45s var(--w-rise);
  }
  .modal::-webkit-scrollbar{display:none;width:0;height:0}
  @keyframes wrise{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
  .m-close{
    position:absolute;top:14px;right:14px;z-index:2;
    width:36px;height:36px;display:grid;place-items:center;
    font-family:var(--mono);font-size:1rem;color:var(--cream);
    border:1px solid var(--hair);border-radius:4px;
    opacity:.75;transition:opacity .2s,border-color .2s;
  }
  .m-close:hover{opacity:1;border-color:var(--brass-edge)}
  .ae-head{padding:26px 28px 20px;border-bottom:1px solid var(--hair)}
  .ae-eyebrow{font-family:var(--mono);font-size:11px;letter-spacing:.28em;text-transform:uppercase;color:var(--brass)}
  .ae-title{font-size:1.6rem;font-weight:300;letter-spacing:-.02em;margin:6px 0 6px;line-height:1.15;text-transform:none}
  .ae-sub{font-size:14px;color:var(--cream-dim);max-width:46ch}
  .ae-body{padding:24px 28px 28px}

  .ae-grid{display:grid;grid-template-columns:1fr 1fr;gap:15px}
  .ae-span2{grid-column:1 / -1}

  .field{display:flex;flex-direction:column;gap:7px;min-width:0;position:relative}
  .field > label{font-family:var(--mono);font-size:10.5px;letter-spacing:.2em;text-transform:uppercase;color:var(--brass)}
  .field .opt{color:var(--brass-edge)}

  /* the well is LIGHTER than the card behind it — darker reads as a hole */
  .field input,.field select,.field textarea{
    font-family:var(--ff);font-size:16px;color:var(--cream);
    background:var(--well);border:0;border-radius:4px;
    padding:13px 14px;width:100%;
    box-shadow:inset 0 0 0 1px var(--brass-edge),
               inset 0 1px 0 var(--brass-lit),
               0 1px 0 rgba(0,0,0,.5),
               0 4px 12px -6px rgba(0,0,0,.7);
    transition:background .16s ease, box-shadow .16s ease;
  }
  .field textarea{resize:vertical;min-height:88px;font-size:15px;line-height:1.55}
  .field input::placeholder,.field textarea::placeholder{color:var(--placeholder);opacity:1}
  .field input:hover,.field select:hover,.field textarea:hover{
    background:var(--well-hover);
    box-shadow:inset 0 0 0 1px var(--brass-lit), inset 0 1px 0 #B08F4E,
               0 1px 0 rgba(0,0,0,.5), 0 4px 12px -6px rgba(0,0,0,.7);
  }
  .field input:focus,.field select:focus,.field textarea:focus{
    outline:none;background:var(--well-focus);
    box-shadow:inset 0 0 0 2px var(--jade-lit), 0 0 0 4px rgba(52,169,107,.16);
  }
  /* FILLED — jade rail; stands down for the error state, or a malformed
     email that is also focused wears the success rail while wrong */
  .field.is-filled:not(.is-invalid) input,
  .field.is-filled:not(.is-invalid) select,
  .field.is-filled:not(.is-invalid) textarea{
    box-shadow:inset 4px 0 0 var(--jade), inset 0 0 0 1px var(--brass-edge),
               inset 0 1px 0 var(--brass-lit), 0 1px 0 rgba(0,0,0,.5);
  }
  .field.is-filled:not(.is-invalid) input:focus,
  .field.is-filled:not(.is-invalid) select:focus,
  .field.is-filled:not(.is-invalid) textarea:focus{
    box-shadow:inset 4px 0 0 var(--jade), inset 0 0 0 2px var(--jade-lit),
               0 0 0 4px rgba(52,169,107,.16);
  }
  /* INVALID — ember rail + message, findable from across the form */
  .field.is-invalid input,.field.is-invalid select,.field.is-invalid textarea{
    box-shadow:inset 4px 0 0 var(--ember), inset 0 0 0 1px #B4523C, 0 1px 0 rgba(0,0,0,.5);
  }
  .field.is-invalid input:focus,.field.is-invalid select:focus,.field.is-invalid textarea:focus{
    box-shadow:inset 4px 0 0 var(--ember), inset 0 0 0 2px var(--ember),
               0 0 0 4px rgba(196,113,63,.18);
  }
  .err{display:none;font-family:var(--mono);font-size:10.5px;letter-spacing:.1em;color:var(--ember-lit);text-transform:uppercase}
  .field.is-invalid .err{display:block}

  /* native control quirks */
  .field:not(.is-filled) input[type="date"]{color:var(--placeholder)}
  .field input[type="number"]{-moz-appearance:textfield;appearance:textfield}
  .field input[type="number"]::-webkit-outer-spin-button,
  .field input[type="number"]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
  .field select{appearance:none;-webkit-appearance:none;cursor:pointer;padding-right:34px}
  .field select option{background:var(--card);color:var(--cream)}
  .select-wrap{position:relative}
  .select-wrap::after{content:"\\25BE";position:absolute;right:13px;bottom:14px;color:var(--brass);pointer-events:none;font-size:12px}
  .field input:-webkit-autofill{
    -webkit-text-fill-color:var(--cream);
    transition:background-color 8000s ease-in-out 0s;
    -webkit-box-shadow:inset 0 0 0 1px var(--brass-edge),
                       inset 0 1px 0 var(--brass-lit),
                       inset 0 0 0 100px var(--well);
  }

  .ae-foot{
    display:flex;align-items:center;justify-content:space-between;gap:16px;
    flex-wrap:wrap;margin-top:22px;padding-top:20px;border-top:1px solid var(--hair);
  }
  .ae-foot p{font-size:12.5px;color:var(--cream-dim);max-width:34ch}
  .btn{
    font-family:var(--mono);font-size:11.5px;letter-spacing:.16em;
    text-transform:uppercase;text-decoration:none;white-space:nowrap;
    border-radius:4px;padding:12px 18px;cursor:pointer;
    display:inline-flex;align-items:center;gap:8px;
    border:1px solid var(--jade);background:var(--jade);color:var(--jade-ink);font-weight:700;
    transition:background .16s ease, box-shadow .2s ease, transform .16s ease;
  }
  .btn:hover{background:var(--jade-lit);box-shadow:0 6px 26px -8px rgba(52,169,107,.8)}
  .btn:active{transform:translateY(1px)}
  .btn[disabled]{opacity:.6;cursor:progress}
  .btn-ghost{background:transparent;color:var(--jade-lit);border-color:#1D5F3D;font-weight:400}
  .btn-ghost:hover{background:rgba(52,169,107,.1);border-color:var(--jade);box-shadow:none}

  /* confirmation */
  .done{display:flex;flex-direction:column;gap:16px;padding:8px 0 4px}
  .done-mark{width:46px;height:46px;border-radius:50%;background:var(--jade);color:var(--jade-ink);display:grid;place-items:center;font-size:22px;font-weight:700}
  .done-h{font-size:1.7rem;font-weight:300;letter-spacing:-.02em;margin:0 0 10px;line-height:1.1;text-transform:none}
  .done-lede{color:var(--cream-dim);font-size:15px;max-width:48ch}
  .done-lede b{color:var(--cream);font-weight:600}
  .summary{display:grid;gap:1px;background:var(--hair);border:1px solid var(--hair);border-radius:3px;overflow:hidden}
  .summary > div{background:var(--night);padding:11px 15px;display:flex;justify-content:space-between;gap:18px;font-size:13.5px}
  .summary dt{font-family:var(--mono);font-size:10.5px;letter-spacing:.18em;text-transform:uppercase;color:var(--brass)}
  .summary dd{text-align:right;color:var(--cream);overflow-wrap:anywhere}
  .done-next{border-top:1px solid var(--hair);padding-top:18px;display:flex;flex-direction:column;gap:12px}
  .done-actions{display:flex;gap:10px;flex-wrap:wrap}

  /* toast */
  .toast{
    position:fixed;left:50%;bottom:2rem;z-index:30;
    transform:translateX(-50%) translateY(16px);
    background:var(--card);color:var(--cream);
    border:1px solid var(--hair);border-radius:4px;
    padding:.9rem 1.3rem;
    font-family:var(--mono);font-size:.66rem;
    letter-spacing:.2em;text-transform:uppercase;
    opacity:0;pointer-events:none;
    transition:opacity .3s var(--w-ease),transform .4s var(--w-rise);
    box-shadow:0 18px 40px rgba(0,0,0,.5);
  }
  .toast.on{opacity:1;transform:translateX(-50%) translateY(0)}
  .toast b{color:var(--brass);font-weight:400}

  /* height budget — the modal fits ONE VIEW, it does not scroll.
     Shrink order: padding, then supporting copy, then the textarea.
     Fields and the submit button never go; values stay 16px (iOS zoom).
     Measured: full form ≈780px with the detail row; the tiers below land
     ≈700px at ≤840h, ≈640px at ≤760h, ≈560px at ≤700h, ≈530px at ≤620h.
     overflow-y:auto stays only as the last resort below ~560px windows. */
  @media(max-height:840px){
    .ae-head{padding:16px 22px 12px}
    .ae-body{padding:14px 22px 18px}
    .ae-grid{gap:12px}
    .field{gap:5px}
    .field input,.field select{padding:11px 13px}
    .field textarea{min-height:64px}
    .ae-foot{margin-top:14px;padding-top:12px}
    .done{gap:12px}
    .summary > div{padding:8px 13px}
  }
  @media(max-height:760px){
    .ae-sub{display:none}
    .ae-title{font-size:1.3rem;margin:4px 0 0}
    .field > label{font-size:10px}
    .field input,.field select{padding:10px 12px}
    .field textarea{min-height:52px}
    .ae-grid{gap:10px}
    .done{gap:10px}
    .done-mark{width:38px;height:38px;font-size:18px}
    .done-h{font-size:1.35rem;margin-bottom:6px}
    .done-lede{font-size:13.5px}
    .summary > div{padding:7px 12px;font-size:12.5px}
    .done-next{padding-top:12px;gap:8px}
  }
  @media(max-height:700px){
    .ae-foot p{display:none}
    .ae-head{padding:11px 20px 9px}
    .ae-body{padding:10px 20px 14px}
    .ae-grid{gap:8px}
    .field{gap:4px}
    .field input,.field select{padding:9px 12px}
    .field textarea{min-height:44px}
    .ae-foot{margin-top:10px;padding-top:10px}
    .done{gap:8px;padding:4px 0 0}
    .done-mark{width:32px;height:32px;font-size:16px}
    .done-h{font-size:1.2rem;margin-bottom:4px}
    .summary > div{padding:5px 11px;font-size:12px}
    .done-next .ae-eyebrow{display:none}
  }
  @media(max-height:620px){
    .ae-eyebrow{font-size:9.5px}
    .ae-title{font-size:1.15rem}
    .field textarea{min-height:38px}
    .btn{padding:10px 16px}
    .m-close{width:30px;height:30px;top:10px;right:10px}
  }
  @media(max-width:620px){
    .ae-grid{grid-template-columns:1fr}
    .ae-head{padding:18px 20px 15px}
    .ae-body{padding:18px 20px 22px}
    .ae-title{font-size:1.35rem}
  }
  @media(max-width:520px){
    .wax-env{width:min(300px,calc(100vw - 1.8rem))}
  }
  @media(prefers-reduced-motion:reduce){
    *{animation:none!important;transition-duration:.01ms!important}
    .w-wax.open .wax-env{max-height:none}
  }
  `;

  var HTML = `
  <div class="w-wax" data-wax>
    <div class="wax-env" data-env>
      <p class="wax-eyebrow"><i></i>R&eacute;servations</p>
      <p class="wax-title">An invitation</p>
      <div class="wax-card">
        <button class="cta primary" data-act="corp">
          <span class="dot"></span>
          <span class="lab"><span class="sub">Priority &middot; Private events</span>Corporate</span>
        </button>
        <a class="cta ghost" data-act="rest" href="${OPENTABLE_URL}" target="_blank" rel="noopener">
          <span class="lab"><span class="sub">OpenTable</span>Reservez</span>
        </a>
        <a class="cta ghost" data-act="call" href="${PHONE_HREF}">
          <span class="lab"><span class="sub">Call the room</span>${PHONE_LABEL}</span>
        </a>
      </div>
    </div>
    <button class="wax-seal" data-seal aria-expanded="false" aria-label="Reservations">?</button>
    <p class="wax-hint" data-hint>Plan an event</p>
  </div>

  <div class="scrim" data-scrim>
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="alia-ae-title">
      <button type="button" class="m-close" data-mclose aria-label="Close">&#10005;</button>

      <div class="ae-head">
        <p class="ae-eyebrow">Private events</p>
        <h3 class="ae-title" id="alia-ae-title" data-ae-title>Request a date</h3>
        <p class="ae-sub" data-ae-sub>We answer the same day, with availability.</p>
      </div>

      <div class="ae-body">
        <form data-form novalidate>
          <div class="ae-grid">
            <div class="field">
              <label for="alia-f-name">Your name</label>
              <input id="alia-f-name" name="name" type="text" autocomplete="name" placeholder="Alex Tremblay" required>
              <span class="err">Required</span>
            </div>
            <div class="field">
              <label for="alia-f-co">Company</label>
              <input id="alia-f-co" name="company" type="text" autocomplete="organization" placeholder="Northbound Capital" required>
              <span class="err">Required</span>
            </div>
            <div class="field">
              <label for="alia-f-email">Work email</label>
              <input id="alia-f-email" name="email" type="email" autocomplete="email" placeholder="alex@northbound.ca" required>
              <span class="err">Enter a valid email</span>
            </div>
            <div class="field">
              <label for="alia-f-phone">Phone</label>
              <input id="alia-f-phone" name="phone" type="tel" autocomplete="tel" placeholder="514 555 0142" required>
              <span class="err">Required</span>
            </div>
            <div class="field">
              <label for="alia-f-type">Event</label>
              <div class="select-wrap">
                <select id="alia-f-type" name="event_type" required>
                  <option value="" selected>Choose one</option>
                  <option>Conference</option>
                  <option>Product launch</option>
                  <option>Client day</option>
                  <option>Year-end party</option>
                  <option>Full buyout</option>
                  <option>Private party</option>
                  <option>Something else</option>
                </select>
              </div>
              <span class="err">Required</span>
            </div>
            <div class="field">
              <label for="alia-f-guests">Guests</label>
              <input id="alia-f-guests" name="guests" type="number" min="1" max="400" inputmode="numeric" placeholder="60" required>
              <span class="err">Required</span>
            </div>
            <div class="field ae-span2" data-detail hidden>
              <label for="alia-f-detail">Event detail</label>
              <input id="alia-f-detail" name="event_detail" type="text" placeholder="Tell us what it is">
              <span class="err">Required</span>
            </div>
            <div class="field">
              <label for="alia-f-date">Preferred date</label>
              <input id="alia-f-date" name="date" type="date" required>
              <span class="err">Required</span>
            </div>
            <div class="field">
              <label for="alia-f-budget">Budget per head <span class="opt">(optional)</span></label>
              <div class="select-wrap">
                <select id="alia-f-budget" name="budget">
                  <option value="" selected>Not sure yet</option>
                  <option>$95 to $150</option>
                  <option>$150 to $225</option>
                  <option>$225 and up</option>
                </select>
              </div>
            </div>
            <div class="field ae-span2">
              <label for="alia-f-notes">Anything we should know <span class="opt">(optional)</span></label>
              <textarea id="alia-f-notes" name="notes" rows="3" placeholder="Dietary needs, AV, a speech, a hard end time."></textarea>
            </div>
          </div>

          <div class="ae-foot">
            <p>We hold provisional dates for 48 hours, no deposit.</p>
            <button class="btn" type="submit" data-msubmit>Send request</button>
          </div>
        </form>

        <div class="done" data-confirm hidden aria-live="polite">
          <div class="done-mark" aria-hidden="true">&#10003;</div>
          <div>
            <h3 class="done-h" data-c-name>Thank you.</h3>
            <p class="done-lede" data-c-lede></p>
          </div>
          <dl class="summary" data-c-grid></dl>
          <div class="done-next">
            <p class="ae-eyebrow">If you need us sooner</p>
            <div class="done-actions">
              <a class="btn btn-ghost" href="${PHONE_HREF}">Call the room &middot; ${PHONE_LABEL}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="toast" data-toast role="status" aria-live="polite"></div>
  `;

  var host = document.createElement('div');
  host.id = 'alia-wgt-host';
  document.body.appendChild(host);
  var root = host.attachShadow({ mode: 'open' });
  root.innerHTML = '<style>' + CSS + '</style>' + HTML;

  // The widget replaces the theme's own sticky fallback dock.
  var hide = document.createElement('style');
  hide.textContent = '.alia-cta-dock{display:none!important}';
  document.head.appendChild(hide);

  var wax     = root.querySelector('[data-wax]');
  var seal    = root.querySelector('[data-seal]');
  var scrim   = root.querySelector('[data-scrim]');
  var form    = root.querySelector('[data-form]');
  var confirmV= root.querySelector('[data-confirm]');
  var toast   = root.querySelector('[data-toast]');
  var typeSel = root.querySelector('#alia-f-type');
  var detailW = root.querySelector('[data-detail]');
  var detailI = root.querySelector('#alia-f-detail');
  var aeTitle = root.querySelector('[data-ae-title]');
  var aeSub   = root.querySelector('[data-ae-sub]');
  var tTimer, lastFocus;

  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  /* toISOString() is UTC and rolls the date near midnight — build locally */
  var now = new Date();
  root.querySelector('#alia-f-date').min =
    now.getFullYear() + '-' + pad2(now.getMonth() + 1) + '-' + pad2(now.getDate());

  function showToast(html) {
    toast.innerHTML = html; // static strings only — user input never lands here
    toast.classList.add('on');
    clearTimeout(tTimer);
    tTimer = setTimeout(function () { toast.classList.remove('on'); }, 3600);
  }

  function setOpen(on) {
    wax.classList.toggle('open', on);
    seal.setAttribute('aria-expanded', on ? 'true' : 'false');
    seal.textContent = on ? '✕' : '?';
  }

  /* ── field state — classes live on the .field wrapper, one tier ── */
  var fields = [].slice.call(form.querySelectorAll('.field'));
  function ctlOf(f) { return f.querySelector('input,select,textarea'); }
  function syncField(f) {
    var c = ctlOf(f); if (!c) return;
    f.classList.toggle('is-filled', String(c.value || '').trim() !== '');
    if (f.dataset.touched === '1') {
      var bad = !c.checkValidity();
      f.classList.toggle('is-invalid', bad);
      c.setAttribute('aria-invalid', String(bad));
    }
  }
  function syncAll() { fields.forEach(syncField); }
  function clearFieldState() {
    fields.forEach(function (f) {
      delete f.dataset.touched;
      f.classList.remove('is-filled', 'is-invalid');
      var c = ctlOf(f); if (c) c.removeAttribute('aria-invalid');
    });
  }
  fields.forEach(function (f) {
    var c = ctlOf(f); if (!c) return;
    c.addEventListener('input',  function () { syncField(f); });
    c.addEventListener('change', function () { syncField(f); });
    c.addEventListener('blur',   function () { f.dataset.touched = '1'; syncField(f); });
  });

  /* Conditional detail — required travels with visibility, so a hidden
     field can never block checkValidity() invisibly. */
  typeSel.addEventListener('change', function () {
    var on = typeSel.value === 'Something else';
    detailW.hidden = !on;
    detailI.required = on;
    if (!on) { detailI.value = ''; detailW.classList.remove('is-filled', 'is-invalid'); delete detailW.dataset.touched; }
  });

  /* ── modal open/close — page locks, scrollbar width compensated ── */
  function lockPage(on) {
    if (on) {
      var sw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = sw > 0 ? sw + 'px' : '';
      document.body.classList.add('menu-open'); // theme's own scroll lock
    } else {
      document.body.classList.remove('menu-open');
      document.body.style.paddingRight = '';
    }
  }
  function openModal() {
    lastFocus = root.activeElement;
    form.hidden = false;
    confirmV.hidden = true;
    aeTitle.textContent = 'Request a date';
    aeSub.textContent = 'We answer the same day, with availability.';
    scrim.classList.add('on');
    lockPage(true);
    setTimeout(function () { root.querySelector('#alia-f-name').focus(); }, 60);
  }
  function closeModal() {
    scrim.classList.remove('on');
    lockPage(false);
    /* reset() clears values but NOT wrapper state — clear both, or the
       next open shows success rails on empty fields */
    form.reset();
    clearFieldState();
    detailW.hidden = true;
    detailI.required = false;
    form.hidden = false;
    confirmV.hidden = true;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  seal.addEventListener('click', function () { setOpen(!wax.classList.contains('open')); });

  root.addEventListener('click', function (e) {
    var b = e.target.closest('[data-act]');
    if (!b) return;
    if (b.getAttribute('data-act') === 'corp') { e.preventDefault(); setOpen(false); openModal(); }
  });

  root.querySelector('[data-mclose]').addEventListener('click', closeModal);
  scrim.addEventListener('click', function (e) { if (e.target === scrim) closeModal(); });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (scrim.classList.contains('on')) closeModal();
    else if (wax.classList.contains('open')) { setOpen(false); seal.focus(); }
  });

  document.addEventListener('click', function (e) {
    if (!wax.classList.contains('open')) return;
    if (e.composedPath().indexOf(wax) === -1) setOpen(false);
  });

  /* Theme buttons carrying data-alia-open-form open the enquiry form.
     Their tel: href stays as the no-JS fallback. */
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-alia-open-form]');
    if (!t) return;
    e.preventDefault();
    openModal();
  });

  /* ── confirmation — user input only ever lands as textContent ── */
  function niceDate(iso) {
    // 'YYYY-MM-DD' parsed by Date() is UTC and renders the previous day
    // west of Greenwich — split and construct locally instead.
    var p = String(iso || '').split('-');
    if (p.length !== 3) return iso || '';
    var d = new Date(+p[0], +p[1] - 1, +p[2]);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
  function showConfirm(raw) {
    var first = String(raw.name || '').trim().split(/\s+/)[0];
    root.querySelector('[data-c-name]').textContent = first ? 'Thank you, ' + first + '.' : 'Thank you.';
    aeTitle.textContent = 'Request received';
    aeSub.textContent = 'One of the events team is already on it.';

    var lede = root.querySelector('[data-c-lede]');
    lede.textContent = '';
    lede.appendChild(document.createTextNode('Your request'));
    if (raw.guests) {
      lede.appendChild(document.createTextNode(' for '));
      var bg = document.createElement('b'); bg.textContent = raw.guests + ' guests';
      lede.appendChild(bg);
    }
    if (raw.date) {
      lede.appendChild(document.createTextNode(' on '));
      var bd = document.createElement('b'); bd.textContent = niceDate(raw.date);
      lede.appendChild(bd);
    }
    lede.appendChild(document.createTextNode(
      ' is with our events team. We will hold the date provisionally while we come back to you.'));

    var grid = root.querySelector('[data-c-grid]');
    grid.textContent = '';
    FIELDS.forEach(function (f) {
      var k = f[1], v = raw[k];
      if (!v) return;
      var row = document.createElement('div');
      var dt = document.createElement('dt'); dt.textContent = LABELS[k] || f[0];
      var dd = document.createElement('dd'); dd.textContent = k === 'date' ? niceDate(v) : v;
      row.appendChild(dt); row.appendChild(dd);
      grid.appendChild(row);
    });

    form.hidden = true;
    confirmV.hidden = false;
    root.querySelector('.modal').scrollTop = 0;
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var btn = form.querySelector('[data-msubmit]');

    fields.forEach(function (f) { if (!f.hidden) f.dataset.touched = '1'; });
    syncAll();
    var firstBad = null;
    fields.forEach(function (f) {
      if (f.hidden || firstBad) return;
      var c = ctlOf(f);
      if (c && !c.checkValidity()) firstBad = c;
    });
    if (firstBad) { firstBad.focus({ preventScroll: false }); return; }

    var raw = {};
    new FormData(form).forEach(function (v, k) { raw[k] = String(v).trim(); });

    /* shared-sheet contract: every value under BOTH the header text and
       its snake_case key, plus Site (store NAME), Page and Status. */
    var data = {};
    FIELDS.forEach(function (f) {
      var v = raw[f[1]] || '';
      data[f[0]] = v;
      data[f[1]] = v;
    });
    data['Site']   = data['site']   = CFG.siteName || window.location.hostname;
    data['Page']   = data['page']   = window.location.pathname;
    data['Status'] = data['status'] = 'New';

    if (!LEAD_ENDPOINT) {
      showConfirm(raw);
      showToast('<b>Demo mode.</b> No lead endpoint wired');
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Sending';
    fetch(LEAD_ENDPOINT, {
      method: 'POST',
      mode: 'no-cors', // Apps Script cannot answer a CORS preflight; response is opaque
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(data)
    }).then(function () {
      showConfirm(raw);
    }).catch(function () {
      showToast('<b>Something went wrong.</b> Call ' + PHONE_LABEL);
    }).then(function () {
      btn.disabled = false;
      btn.textContent = 'Send request';
    });
  });

  /* Reveal only once the theme's preloader has cleared. */
  (function reveal() {
    var SEL = '#preloader,[class*="preloader" i],[id*="preload" i]';
    var pre = document.querySelector(SEL);
    function show() { wax.classList.add('revealed'); }
    if (!pre) {
      if (document.readyState === 'complete') setTimeout(show, 700);
      else window.addEventListener('load', function () { setTimeout(show, 700); });
      return;
    }
    var start = +new Date();
    var iv = setInterval(function () {
      var el = document.querySelector(SEL), gone = !el;
      if (el) {
        var cs = getComputedStyle(el);
        gone = cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.1;
      }
      if (gone || (+new Date() - start) > 6000) { show(); clearInterval(iv); }
    }, 200);
  })();
})();
